import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Typography,
  useTheme,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  type GridColumns,
  type GridValidRowModel,
  type GridEventListener,
  type GridRowParams,
  type MuiEvent,
  type GridSortModel,
} from '@mui/x-data-grid';
import { DialogBox } from 'components/shared';
import { strings } from 'i18n/Strings';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllOrganizationAdmins, removeEmployee } from 'service/UserService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { colorTheme } from '../../../theme';
import AlertModal from '../../../components/shared/AlertModal';
import { forgotPassword } from 'service';
import { CustomDataGrid } from 'components';
import { useParams } from 'react-router-dom';
import CreateEmployee, {
  type IEmployeeInfo,
} from 'pages/Employer/Employees/CreateEmployee';
import { roles } from 'constants/constantText';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';
import ConfirmationModal from 'components/confirmation-modal';

type GridRowData = Record<string, unknown>;

const AdminGrid = (): JSX.Element => {
  const { orgId } = useParams();
  const currentTheme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(currentTheme.palette.mode);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [employerAdminData, setEmployerAdminData] = useState<GridRowData[]>([]);
  const [updateEmployeeId, setUpdateEmployeeId] = useState('');
  const [comments, setComments] = useState('');
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const [disabledInviteButtons, setDisabledInviteButtons] = useState({});
  const [selectedData, setSelectedData] = useState<
    GridValidRowModel | undefined
  >();

  const ref = useRef<HTMLInputElement>(null);
  const pageInfo = useSelector((state) => state?.pagination?.employees);
  const [searchValue, setSearchValue] = useState('');
  const updateEmployeeData = (): void => {
    if (orgId) {
      getAllOrganizationAdmins({
        id: orgId.toString(),
        roleName: roles.OrganizationAdminRole,
        page: Number(pageInfo?.page) + 1,
        size: pageInfo?.pageSize,
        sortBy: pageInfo?.sortBy,
        sortOrder: pageInfo?.sortOrder,
        searchValue,
      })
        .then((res) => {
          if (typeof res !== 'undefined' && 'basicInfo' in res) {
            const { basicInfo } = res as { basicInfo: GridRowData[] };
            if (basicInfo) {
              setEmployerAdminData(basicInfo?.rows);
              setPageCount(basicInfo?.count);
            }
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
  };

  useEffect(() => {
    updateEmployeeData();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);

  const handleResentInvite = (email: string, empId: number): void => {
    if (email != null) {
      void (async () => {
        await forgotPassword(email)
          .then(() => {
            setDisabledInviteButtons((prev) => ({
              ...prev,
              [empId]: 30,
            }));
            const interval = setInterval(() => {
              setDisabledInviteButtons((prev: unknown) => {
                if (prev[empId] > 1) {
                  return {
                    ...prev,
                    [empId]: prev[empId] - 1,
                  };
                } else {
                  clearInterval(interval);
                  const { [empId]: _, ...rest } = prev; // Remove key after countdown
                  return rest;
                }
              });
            }, 1000);
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.resetLinkSuccessText,
              }),
            );
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.message }),
            );
          });
      })();
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'firstName',
      flex: 0.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.firstName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.firstName}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      flex: 0.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.lastName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.lastName}
        </Typography>
      ),
    },
    {
      field: 'email',
      flex: 0.9,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.email}
        </Typography>
      ),
    },
    {
      field: 'phone',
      flex: 0.7,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.phoneNumber}
        </Typography>
      ),
      renderCell: ({ row }) => {
        let displayPhone = row?.phone ? row.phone : '-';
        if (displayPhone !== '-' && displayPhone.startsWith('++')) {
          displayPhone = displayPhone.substring(1);
        }
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {displayPhone}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      flex: 0.4,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status === 'Leaver'
            ? 'Leaver'
            : row.isActive
            ? 'Active'
            : 'In-Active'}
        </Typography>
      ),
    },
    {
      field: 'resendInvite',
      headerName: strings.displayText.resendInvite,
      headerAlign: 'center',
      sortable: false,
      flex: 0.6,
      renderCell: ({ row }) => (
        <Box m="0 auto" display="flex" justifyContent="space-evenly">
          {!row.emailVerifiedAt ? (
            <Button
              sx={{ padding: '4px 5px' }}
              variant="outlined"
              color="success"
              disabled={disabledInviteButtons[row.empId] !== undefined}
              onClick={() => {
                handleResentInvite(row.email, row.empId);
              }}
              endIcon={<SendIcon />}
            >
              {disabledInviteButtons[row.empId] !== undefined
                ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  `Wait ${disabledInviteButtons[row.orgEmpId || row.empId]}s`
                : strings.displayText.invite}
            </Button>
          ) : (
            <Button
              sx={{ padding: '4px 5px' }}
              variant="outlined"
              color="success"
              disabled={true}
              endIcon={<SendIcon />}
            >
              {strings.displayText.invite}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const handleConfirmationClose = (): void => {
    setOpenCreateDialog(false);
    setConfirmationModal(false);
  };

  const handleContinuePress = (): void => {
    setConfirmationModal(false);
  };

  const handleCreateDialogClose = (): void => {
    setConfirmationModal(true);
  };

  const handleCreateEmployeeClose = (): void => {
    setOpenCreateDialog(false);
  };

  const handleUpdateDialogClose = (): void => {
    setOpenUpdateDialog(false);
  };

  const handleDeleteDialogClose = (): void => {
    setOpenDeleteDialog(false);
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedData?.empId !== undefined) {
      removeEmployee(Number(selectedData.empId), comments)
        .then((res) => {
          if (res) {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: res?.msg,
              }),
            );
            res && updateEmployeeData();
            setOpenDeleteDialog(false);
          }
        })
        .catch((error) => {
          const message = error?.response?.data?.message;
          console.log(error, message);

          dispatch(
            setSnackBarFailed({
              snackBarMessage: message || 'User deleted failed',
            }),
          );
        });
    }
  };
  const handleEvent: GridEventListener<'rowClick'> = (
    params: GridRowParams, // GridRowParams
    event: MuiEvent<React.MouseEvent<HTMLElement>>, // MuiEvent<React.MouseEvent<HTMLElement>>
  ) => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    if (params?.row?.status === 'Leaver') {
      setOpenCommentDialog(true);
      setComments(params?.row?.comments);
      event.preventDefault();
    }
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'employers', value: 0 }));
  };

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'employees',
        value: model[0].field,
      }),
    );
    dispatch(
      setSortOrder({
        key: 'employees',
        value: model[0].sort,
      }),
    );
    dispatch(setCurrentPage({ key: 'employees', value: 0 }));
  };

  return (
    <Fragment>
      <CustomDataGrid
        title="Admins"
        columns={columns}
        rows={employerAdminData}
        searchEnabled={true}
        handleSearch={handleSearch}
        onRowClick={handleEvent}
        addButtonTitle={'Add Admin'}
        onAddButtonClick={() => {
          setOpenCreateDialog(true);
          ref.current?.focus();
          setUpdateEmployeeId('');
        }}
        pageCount={pageCount}
        actionBtnText1="edit"
        actionBtnText2="delete"
        ActionBtnIcon1={() => <EditIcon />}
        ActionBtnIcon2={() => <DeleteIcon />}
        onActionBtnClick1={(row, event) => {
          if (row?.status === 'Leaver') return;
          event.stopPropagation();
          setSelectedData(row);
          setOpenUpdateDialog(true);
          setUpdateEmployeeId(row.empId);
        }}
        onActionBtnClick2={(row, event) => {
          if (row?.status === 'Leaver') return;
          event.stopPropagation();
          setSelectedData(row);
          setOpenDeleteDialog(true);
        }}
        getRowId={(row: GridRowData) =>
          `${String(row.empId)}${row.email ? String(row.email) : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="employees"
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          handleSortChange(mode);
        }}
        searchPlaceholder={strings.displayText.searchLabel1}
      />

      <DialogBox
        open={openCreateDialog}
        onClose={handleCreateDialogClose}
        screenWidth="md"
        title={
          updateEmployeeId
            ? strings.displayText.editAdmin
            : strings.displayText.createAdmin
        }
      >
        {/* <CreateAdminForm
          onClose={handleCreateDialogClose}
          updateEmployeeData={updateEmployeeData}
        /> */}
        <CreateEmployee
          from="admin"
          organizationId={orgId}
          updateEmployeeData={updateEmployeeData}
          onClose={handleCreateEmployeeClose}
        />
      </DialogBox>

      <DialogBox
        open={openUpdateDialog}
        onClose={handleUpdateDialogClose}
        screenWidth="md"
        title="Edit Admin"
      >
        {/* <UpdateAdminForm
          data={selectedData as GridValidRowModel}
          onClose={handleUpdateDialogClose}
          updateEmployeeData={updateEmployeeData}
        /> */}
        <CreateEmployee
          from="admin"
          organizationId={orgId}
          updateEmployeeId={updateEmployeeId}
          updateEmployeeData={updateEmployeeData}
          onClose={handleUpdateDialogClose}
          selectedData={selectedData as IEmployeeInfo}
        />
      </DialogBox>
      <AlertModal
        isOrganizationEmployee={true}
        comments={comments}
        setComments={setComments}
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        handleDelete={handleDelete}
        isShowAlertMessage={employerAdminData?.length === 1}
        title={
          employerAdminData?.length === 1
            ? strings.displayText.deleteAlertMessage
            : `${strings.displayText.deleteConfirmationBAdminTextNew}`
        }
        email={String(selectedData?.email)}
      />
      <Dialog
        open={openCommentDialog}
        onClose={() => {
          setComments('');
          setOpenCommentDialog(false);
        }}
      >
        <DialogContent>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center' }}>
            {strings.displayText.adminSuspendedMessage}
          </Typography>
          {comments ? (
            <Typography
              mt={2}
              component="h1"
              variant="h5"
              style={{ fontSize: '16px' }}
            >
              {' '}
              {strings.displayText.comments}: {comments}
            </Typography>
          ) : (
            <Typography
              mt={2}
              component="h1"
              variant="h5"
              style={{ fontSize: '16px' }}
            >
              {strings.displayText.noCommentsProvid}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationModal
        open={confirmationModal}
        onClose={handleConfirmationClose}
        onContinue={handleContinuePress}
      />
    </Fragment>
  );
};

export default AdminGrid;
